/**
 * StepPipeline — horizontal series of coloured dots and labels connected by
 * chevrons, used to summarise a process flow at a glance.
 *
 * Use when: rendering a process definition card, an incident timeline summary,
 * or a checklist progress indicator. For full timelines with timestamps use
 * the case timeline; for big multi-step UIs use the Process Designer.
 */
import { Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ChevronRightRegular } from '@fluentui/react-icons';

export type StepState = 'pending' | 'active' | 'done' | 'error' | 'skipped';

const TONE_TOKEN: Record<StepState, string> = {
  pending: tokens.colorNeutralForeground4,
  active: tokens.colorBrandForeground1,
  done: tokens.colorPaletteGreenForeground1,
  error: tokens.colorPaletteRedForeground1,
  skipped: tokens.colorNeutralForeground3,
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  step: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  active: {
    boxShadow: `0 0 0 3px ${tokens.colorBrandBackground2}`,
  },
  arrow: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  label: {
    color: tokens.colorNeutralForeground1,
  },
  labelMuted: {
    color: tokens.colorNeutralForeground3,
  },
});

export interface PipelineStep {
  key: string;
  label: string;
  state: StepState;
}

interface StepPipelineProps {
  steps: PipelineStep[];
  /** When true, hides labels for compact card displays. */
  compact?: boolean;
}

export function StepPipeline({ steps, compact }: StepPipelineProps) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      {steps.map((step, i) => (
        <span key={step.key} className={styles.step}>
          <span
            className={mergeClasses(styles.dot, step.state === 'active' && styles.active)}
            style={{ backgroundColor: TONE_TOKEN[step.state] }}
            aria-label={`${step.label}: ${step.state}`}
          />
          {!compact ? (
            <Caption1 className={step.state === 'pending' || step.state === 'skipped' ? styles.labelMuted : styles.label}>
              {step.label}
            </Caption1>
          ) : null}
          {i < steps.length - 1 ? <ChevronRightRegular className={styles.arrow} /> : null}
        </span>
      ))}
    </div>
  );
}
